/**
 * ERL → IRL strategy for the dashboard backtest workspace.
 * Sweep external range liquidity → enter at internal FVG → target opposing ERL.
 */

import { atrFromBars } from '../indicators/atr';
import { fairValueGaps } from '../indicators/fvg';
import { normalizeBars } from '../engine/bars';
import { paramsFromSchema } from './params';

function isPivotHigh(bars, i, left, right) {
  const h = bars[i]?.high;
  if (!Number.isFinite(h)) return false;
  for (let j = i - left; j < i; j++) {
    if (!Number.isFinite(bars[j]?.high) || bars[j].high >= h) return false;
  }
  for (let j = i + 1; j <= i + right; j++) {
    if (!Number.isFinite(bars[j]?.high) || bars[j].high > h) return false;
  }
  return true;
}

function isPivotLow(bars, i, left, right) {
  const l = bars[i]?.low;
  if (!Number.isFinite(l)) return false;
  for (let j = i - left; j < i; j++) {
    if (!Number.isFinite(bars[j]?.low) || bars[j].low <= l) return false;
  }
  for (let j = i + 1; j <= i + right; j++) {
    if (!Number.isFinite(bars[j]?.low) || bars[j].low < l) return false;
  }
  return true;
}

/**
 * Expanding confirmed pivots → most recent swing high/low as dealing range.
 * Pivot at i is only known at i + pivotBars (right confirmation).
 */
export function rangeSeriesFromBars(bars, { lookback = 50, pivotBars = 10 } = {}) {
  const n = bars?.length || 0;
  const rangeHigh = new Array(n).fill(null);
  const rangeLow = new Array(n).fill(null);
  const confirmedHighs = [];
  const confirmedLows = [];

  for (let i = 0; i < n; i++) {
    const confirmIdx = i - pivotBars;
    if (confirmIdx >= pivotBars) {
      if (isPivotHigh(bars, confirmIdx, pivotBars, pivotBars)) {
        confirmedHighs.push({ index: confirmIdx, price: bars[confirmIdx].high });
      }
      if (isPivotLow(bars, confirmIdx, pivotBars, pivotBars)) {
        confirmedLows.push({ index: confirmIdx, price: bars[confirmIdx].low });
      }
    }

    const windowStart = Math.max(0, i - lookback);
    const highs = confirmedHighs.filter((p) => p.index >= windowStart && p.index <= i);
    const lows = confirmedLows.filter((p) => p.index >= windowStart && p.index <= i);
    rangeHigh[i] = highs.length ? highs[highs.length - 1].price : null;
    rangeLow[i] = lows.length ? lows[lows.length - 1].price : null;
  }

  return { rangeHigh, rangeLow };
}

function barTouchesZone(bar, top, bottom) {
  if (!bar || !Number.isFinite(top) || !Number.isFinite(bottom)) return false;
  return bar.low <= top && bar.high >= bottom;
}

function recentSweep(bars, i, rangeHigh, rangeLow, lookback = 12) {
  if (!Number.isFinite(rangeHigh) || !Number.isFinite(rangeLow)) return null;
  const start = Math.max(0, i - lookback);
  let last = null;
  for (let j = start; j <= i; j++) {
    const bar = bars[j];
    if (!bar) continue;
    const bodyHigh = Math.max(bar.open, bar.close);
    const bodyLow = Math.min(bar.open, bar.close);
    if (bar.high > rangeHigh && bodyHigh < rangeHigh) {
      last = { side: 'short', sweptPrice: rangeHigh, index: j };
    }
    if (bar.low < rangeLow && bodyLow > rangeLow) {
      last = { side: 'long', sweptPrice: rangeLow, index: j };
    }
  }
  return last;
}

/**
 * After a sweep, the gap must form within `depthPct` of the range, measured from
 * the swept side. 50 means at or before equilibrium. 100 allows the whole range.
 */
export function gapWithinSweepDepth(fvg, side, rangeHigh, rangeLow, depthPct) {
  if (!fvg) return false;
  const pct = Number(depthPct);
  if (!Number.isFinite(pct) || pct >= 100) return true;
  if (pct <= 0) return false;
  if (!Number.isFinite(rangeHigh) || !Number.isFinite(rangeLow) || rangeHigh <= rangeLow) return false;
  const span = rangeHigh - rangeLow;
  const mid = (fvg.top + fvg.bottom) / 2;
  if (side === 'long') return mid <= rangeLow + span * (pct / 100);
  if (side === 'short') return mid >= rangeHigh - span * (pct / 100);
  return false;
}

function latestMatchingFvg(fvgSeries, i, side, afterIndex) {
  let found = null;
  for (let j = Math.max(2, afterIndex + 1); j <= i; j++) {
    const g = fvgSeries[j];
    if (!g) continue;
    const want = side === 'long' ? 'Long' : 'Short';
    if (g.direction !== want) continue;
    found = { ...g, index: j };
  }
  return found;
}

/**
 * Entry checklist for one bar. Same rules as onBar, without mutating used-zone state.
 * A long needs a prior sweep of the range low, then price back inside a bullish FVG
 * that formed after that sweep. A short is the mirror (range high + bearish FVG).
 */
export function describeErlIrlSetup({ bars, ind, params, i, usedKeys } = {}) {
  const bar = bars?.[i];
  const rh = ind?.rangeHighNow ?? ind?.rangeHigh?.[i];
  const rl = ind?.rangeLowNow ?? ind?.rangeLow?.[i];
  const atr = ind?.atrNow ?? ind?.atr?.[i];
  const rangeReady = Number.isFinite(rh) && Number.isFinite(rl) && rh > rl;

  const sweep = rangeReady ? recentSweep(bars, i, rh, rl, params?.sweepLookback) : null;
  const sweepPrior = Boolean(sweep && sweep.index < i);
  const fvg = sweepPrior ? latestMatchingFvg(ind?.fvg || [], i, sweep.side, sweep.index) : null;
  const depthPct = Number.isFinite(Number(params?.gapDepthPct)) ? Number(params.gapDepthPct) : 50;
  const depthOk = Boolean(fvg && gapWithinSweepDepth(fvg, sweep.side, rh, rl, depthPct));
  const atIrl = Boolean(fvg && barTouchesZone(bar, fvg.top, fvg.bottom));
  const zoneKey = fvg ? `${fvg.index}:${fvg.top}:${fvg.bottom}` : null;
  const fresh = Boolean(zoneKey && !usedKeys?.[zoneKey]);

  const buffer = Number.isFinite(atr)
    ? atr * (params?.atrMultiple || 0)
    : (rangeReady ? Math.abs(rh - rl) * 0.001 : 0);
  const entry = fvg ? (fvg.top + fvg.bottom) / 2 : null;

  let stopPrice = null;
  let target = null;
  let riskOk = false;
  if (atIrl && sweep?.side === 'long') {
    stopPrice = Math.min(sweep.sweptPrice, fvg.bottom) - buffer;
    target = rh;
    riskOk = entry > stopPrice && target > entry;
  } else if (atIrl && sweep?.side === 'short') {
    stopPrice = Math.max(sweep.sweptPrice, fvg.top) + buffer;
    target = rl;
    riskOk = entry < stopPrice && target < entry;
  }

  const side = sweep?.side === 'short' ? 'short' : sweep?.side === 'long' ? 'long' : null;
  const ready = Boolean(rangeReady && sweepPrior && fvg && depthOk && atIrl && fresh && riskOk && side);

  const items = [
    {
      id: 'range',
      label: rangeReady ? 'Dealing range set' : 'Dealing range',
      pass: rangeReady,
    },
    {
      id: 'sweep',
      label: sweepPrior
        ? (side === 'long' ? 'Swept ERL low' : 'Swept ERL high')
        : 'ERL sweep',
      pass: sweepPrior,
    },
    {
      id: 'fvg',
      label: fvg ? `${fvg.direction} FVG after sweep` : 'IRL fair value gap',
      pass: Boolean(fvg),
    },
    {
      id: 'depth',
      label: depthPct >= 100 ? 'Gap anywhere in range' : `Gap within ${depthPct}% of sweep`,
      pass: depthOk,
    },
    {
      id: 'touch',
      label: 'Price at IRL',
      pass: atIrl,
    },
    {
      id: 'risk',
      label: side === 'short'
        ? 'Stop above sweep, target ERL low'
        : 'Stop under sweep, target ERL high',
      pass: riskOk,
    },
  ];

  return {
    items,
    ready,
    side: ready ? side : null,
    entry: ready ? entry : null,
    stopPrice: ready ? stopPrice : null,
    target: ready ? target : null,
    zoneKey: ready ? zoneKey : null,
  };
}

export const erlIrl = {
  id: 'erl-irl',
  name: 'ERL / IRL',
  indicators: ['FVG', 'ATR'],
  defaultTradeSession: { preset: 'rth', start: '09:30', end: '16:00' },
  chartOverlays(params = {}) {
    return [
      { key: 'rangeHigh', label: 'ERL High', colorKey: 'danger', lineWidth: 2 },
      { key: 'rangeLow', label: 'ERL Low', colorKey: 'success', lineWidth: 2 },
    ];
  },
  paramSchema: [
    { key: 'lookback', label: 'Range lookback', type: 'number', default: 50, min: 20, max: 200, step: 1, section: 'Range' },
    { key: 'pivotBars', label: 'Pivot bars', type: 'number', default: 5, min: 2, max: 20, step: 1, section: 'Range' },
    { key: 'sweepLookback', label: 'Sweep lookback', type: 'number', default: 12, min: 3, max: 40, step: 1, section: 'Setup' },
    { key: 'fvgMinSize', label: 'FVG min', type: 'number', default: 5, min: 0, max: 50, step: 0.1, section: 'Setup' },
    { key: 'gapDepthPct', label: 'Gap depth %', type: 'number', default: 50, min: 1, max: 100, step: 1, section: 'Setup' },
    { key: 'atrPeriod', label: 'ATR period', type: 'number', default: 14, min: 2, max: 200, step: 1, section: 'Risk' },
    { key: 'atrMultiple', label: 'ATR stop buffer', type: 'number', default: 0.25, min: 0, max: 5, step: 0.05, section: 'Risk' },
    { key: 'tp2R', label: 'Fallback TP R', type: 'number', default: 2, min: 0.5, max: 10, step: 0.1, section: 'Risk' },
  ],

  prepare(bars, params) {
    const range = rangeSeriesFromBars(bars, {
      lookback: params.lookback,
      pivotBars: params.pivotBars,
    });
    return {
      ind: {
        atr: atrFromBars(bars, params.atrPeriod),
        fvg: fairValueGaps(bars, { minSize: params.fvgMinSize, maxSize: Infinity }),
        rangeHigh: range.rangeHigh,
        rangeLow: range.rangeLow,
      },
      state: {
        usedFvgKeys: {},
      },
    };
  },

  explain(ctx) {
    const setup = describeErlIrlSetup({
      bars: ctx.bars,
      ind: ctx.ind,
      params: ctx.params,
      i: ctx.i,
      usedKeys: ctx.state?.usedFvgKeys,
    });
    return setup.items;
  },

  onBar(ctx) {
    const { i, ind, params, position, state } = ctx;
    if (position) return [];

    const setup = describeErlIrlSetup({
      bars: ctx.bars,
      ind,
      params,
      i,
      usedKeys: state?.usedFvgKeys,
    });
    if (!setup.ready) return [];

    state.usedFvgKeys[setup.zoneKey] = true;
    const risk = setup.side === 'long'
      ? setup.entry - setup.stopPrice
      : setup.stopPrice - setup.entry;
    const reward = setup.side === 'long'
      ? setup.target - setup.entry
      : setup.entry - setup.target;
    const rToErl = risk > 0 ? reward / risk : params.tp2R;

    return [{
      type: 'enter',
      orderType: 'market',
      side: setup.side,
      stopPrice: setup.stopPrice,
      takeProfits: [
        { id: 'tp1', rMultiple: 1, qtyPct: 0.5 },
        { id: 'tp2', rMultiple: Math.max(Number(rToErl) || params.tp2R, 1), qtyPct: 0.5 },
      ],
    }];
  },
};

function sliceIndicators(ind, i) {
  const out = {};
  if (!ind) return out;
  for (const [key, series] of Object.entries(ind)) {
    out[key] = series;
    out[`${key}Now`] = Array.isArray(series) ? series[i] : series;
  }
  return out;
}

/** Live read of the same checklist the backtest uses, on the latest bar. */
export function evaluateErlIrlLive(rawBars, paramOverrides = {}, unit = 'Minute') {
  const bars = normalizeBars(rawBars, unit);
  const params = paramsFromSchema(erlIrl.paramSchema, paramOverrides);
  if (bars.length < 3) {
    return describeErlIrlSetup({ bars, ind: {}, params, i: Math.max(0, bars.length - 1), usedKeys: {} });
  }
  const prepared = erlIrl.prepare(bars, params);
  const i = bars.length - 1;
  return describeErlIrlSetup({
    bars,
    ind: sliceIndicators(prepared.ind, i),
    params,
    i,
    usedKeys: {},
  });
}

export default erlIrl;
